'use client';
/**
 * UNE CALIBRATION TRACÉE DEPUIS LE STOCK (spec 002, récit 11 — 04/11/2026).
 *
 * Demande d'Ines (03/11/2026) : « Aucune saisie libre. Tout en liste déroulante
 * depuis le stock : un article déstocké, et en grisé les autres articles
 * disponibles mais non déstockés. »
 *
 * Trois points d'entrée montent CE composant — la saisie de l'écran
 * Calibrations, la calibration lancée depuis le CQI, la correction RQ — pour
 * qu'une seule règle s'applique (EF-063). Les listes viennent du moteur
 * (`GET ged/calibrations/stock`, `backend/src/calibration-stock.ts`) : c'est la
 * même structure qu'il contrôle à l'écriture, l'écran ne décide de rien.
 *
 *  · en tête, les lots DÉSTOCKÉS (en service, ou flacon de calibrant
 *    reconstitué dans sa stabilité) : sélectionnables ;
 *  · en dessous, en grisé et désactivés : acceptés non déstockés (avec
 *    « Déstocker ce lot », la transition existante `accepte → en_service`,
 *    mêmes droits), en quarantaine, périmés, stabilité dépassée — motif dit ;
 *  · n° de lot et péremption s'affichent en lecture : on ne les tape plus.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { inputStyle, Selecteur } from '@/components/ui';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { Bouton } from '@/shared/Bouton';

export type RoleLot = 'reactif' | 'calibrant' | 'controle';
export interface OptionLot {
  cle: string; lotId: string; ref: string; flaconRef: string | null; produitCode: string; libelle: string;
  role: RoleLot; niveau: string; numeroLot: string; datePeremption: string; statut: string;
  dateDestockage: string; finStabilite: string; selectionnable: boolean; motif: string; action: 'destocker' | null;
}
export interface StockCalibration {
  equipementCode: string; date: string; stockActif: boolean;
  reactifs: OptionLot[]; calibrants: { niveau: string; options: OptionLot[] }[]; controles: OptionLot[];
}
/** Les choix d'une saisie, par clé d'option (`lotRef` ou `lotRef#flaconRef`). */
export interface ChoixLots { reactifs: string[]; calibrants: Record<string, string>; controle: string }
export const CHOIX_VIDE: ChoixLots = { reactifs: [], calibrants: {}, controle: '' };
export interface LotUtiliseDemande { role: RoleLot; niveau?: string; lotRef: string; flaconRef?: string }

/** Le corps `lotsUtilises` envoyé au moteur, depuis les choix de l'écran. */
export function lotsUtilisesDe(stock: StockCalibration | null, choix: ChoixLots): LotUtiliseDemande[] {
  if (!stock) return [];
  const trouve = (liste: OptionLot[], cle: string) => liste.find((o) => o.cle === cle);
  const out: LotUtiliseDemande[] = [];
  const pousser = (o: OptionLot | undefined, niveau?: string) => {
    if (!o) return;
    out.push({ role: o.role, ...(niveau ? { niveau } : {}), lotRef: o.ref, ...(o.flaconRef ? { flaconRef: o.flaconRef } : {}) });
  };
  for (const c of choix.reactifs) if (c) pousser(trouve(stock.reactifs, c));
  for (const g of stock.calibrants) {
    const c = choix.calibrants[g.niveau];
    if (c) pousser(trouve(g.options, c), g.niveau);
  }
  if (choix.controle) pousser(trouve(stock.controles, choix.controle));
  return out;
}

/** Les choix déjà portés par une calibration (pour la correction). */
export function choixDepuisLots(lots: unknown): ChoixLots {
  const out: ChoixLots = { reactifs: [], calibrants: {}, controle: '' };
  if (!Array.isArray(lots)) return out;
  for (const l of lots as Record<string, unknown>[]) {
    const cle = `${String(l?.lotRef || '')}${l?.flaconRef ? `#${String(l.flaconRef)}` : ''}`;
    if (l?.role === 'reactif') out.reactifs.push(cle);
    else if (l?.role === 'calibrant') out.calibrants[String(l?.niveau || '')] = cle;
    else if (l?.role === 'controle') out.controle = cle;
  }
  return out;
}

/** Les listes de l'appareil, à la date de la calibration (la péremption se juge à cette date). */
export function useStockCalibration(equipement: string, date: string) {
  const [stock, setStock] = useState<StockCalibration | null>(null);
  const [etat, setEtat] = useState<'vide' | 'charge' | 'pret' | 'echec'>('vide');
  const [tour, setTour] = useState(0);
  useEffect(() => {
    /* Effacer AVANT de recharger : sans cela, le formulaire montrerait les lots
       de l'appareil précédent pendant la requête (même faux positif assumé que
       `ChoixParametre`, Calibrations.tsx). */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!equipement) { setStock(null); setEtat('vide'); return undefined; }
    let vivant = true;
    setEtat('charge');
    const q = new URLSearchParams({ equipement, ...(date ? { date } : {}) });
    void fetch(`/api/ged/ged/calibrations/stock?${q.toString()}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: StockCalibration) => { if (vivant) { setStock(d); setEtat('pret'); } })
      .catch(() => { if (vivant) { setStock(null); setEtat('echec'); } });
    return () => { vivant = false; };
  }, [equipement, date, tour]);
  const recharger = useCallback(() => setTour((n) => n + 1), []);
  return { stock, etat, recharger };
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 };

function libelleOption(o: OptionLot): string {
  const lot = o.numeroLot ? `lot ${o.numeroLot}` : 'lot sans numéro';
  const flacon = o.flaconRef ? ` · flacon ${o.flaconRef}` : '';
  const per = o.datePeremption ? ` · pér. ${fmtDate(o.datePeremption)}` : '';
  return `${o.libelle} — ${lot}${flacon}${per}${o.selectionnable ? '' : ` (${o.motif})`}`;
}

/** « Déstocker ce lot » : la transition existante, confirmée sur place. */
function Destocker({ o, onFait }: { o: OptionLot; onFait: () => void }) {
  const { smq } = useGed();
  const [confirmer, setConfirmer] = useState(false);
  const [occupe, setOccupe] = useState(false);
  if (!o.lotId) return null;
  if (!confirmer) {
    return (
      <Bouton taille="sm" variante="discret" onClick={() => setConfirmer(true)}>Déstocker ce lot</Bouton>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--text-body)' }}>Mettre en service le lot {o.numeroLot || o.ref} aujourd’hui ?</span>
      <Bouton taille="sm" variante="primaire" disabled={occupe} onClick={() => {
        setOccupe(true);
        void smq(`ged/lots/${o.lotId}/status`, { statut: 'en_service' }, `Lot ${o.numeroLot || o.ref} mis en service.`)
          .then((ok) => { setOccupe(false); setConfirmer(false); if (ok) onFait(); });
      }}>{occupe ? 'Mise en service…' : 'Confirmer'}</Bouton>
      <Bouton taille="sm" onClick={() => setConfirmer(false)} disabled={occupe}>Non</Bouton>
    </span>
  );
}

/** Une liste : un rôle (réactif, contrôle) ou un niveau de calibrant. */
export function ChoixLotStock({ id, label, requis, options, valeur, onChange, onDestocke, exclure = [] }: {
  id: string; label: string; requis?: boolean; options: OptionLot[];
  valeur: string; onChange: (cle: string) => void; onDestocke: () => void;
  /** Clés déjà choisies dans une autre liste du même rôle. */
  exclure?: string[];
}) {
  const visibles = options.filter((o) => o.cle === valeur || !exclure.includes(o.cle));
  const choisis = visibles.filter((o) => o.selectionnable);
  const grises = visibles.filter((o) => !o.selectionnable);
  const courant = options.find((o) => o.cle === valeur);
  const aDestocker = grises.filter((o) => o.action === 'destocker');
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label style={lbl} htmlFor={id}>{label}{requis && <span style={{ color: 'var(--red-600)' }}> *</span>}</label>
      {options.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun lot de cet appareil dans le stock.</div>
      ) : (
        <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle}>
          <option value="">—</option>
          {choisis.length > 0 && (
            <optgroup label="Déstockés">
              {choisis.map((o) => <option key={o.cle} value={o.cle}>{libelleOption(o)}</option>)}
            </optgroup>
          )}
          {grises.length > 0 && (
            <optgroup label="Non choisissables">
              {grises.map((o) => <option key={o.cle} value={o.cle} disabled>{libelleOption(o)}</option>)}
            </optgroup>
          )}
        </Selecteur>
      )}
      {courant && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          N° de lot <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>{courant.numeroLot || '—'}</strong>
          {' · '}péremption {courant.datePeremption ? fmtDate(courant.datePeremption) : 'non renseignée'}
          {courant.dateDestockage && ` · ${courant.flaconRef ? 'reconstitué' : 'déstocké'} le ${fmtDate(courant.dateDestockage)}`}
          {courant.finStabilite && ` · stable jusqu’au ${fmtDate(courant.finStabilite)}`}
        </div>
      )}
      {choisis.length === 0 && options.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>Aucun lot déstocké : déstockez-en un, ou voyez le motif dans la liste.</div>
      )}
      {aDestocker.map((o) => (
        <div key={o.cle} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-faint)' }}>
          <span>{o.libelle} — lot {o.numeroLot || o.ref} : non déstocké.</span>
          <Destocker o={o} onFait={onDestocke} />
        </div>
      ))}
    </div>
  );
}

/** Réactif(s), calibrant niveau par niveau, contrôle — le bloc des trois formulaires. */
export function LotsCalibration({ stock, choix, onChange, onDestocke, controleRequis, prefixe = 'cal-stock' }: {
  stock: StockCalibration; choix: ChoixLots; onChange: (c: ChoixLots) => void; onDestocke: () => void;
  controleRequis?: boolean; prefixe?: string;
}) {
  /* Plusieurs réactifs possibles (le D10 en a deux : le pack et le primer) :
     une liste de plus apparaît dès que la précédente est remplie, tant qu'il
     reste un lot déstocké à choisir. */
  const pris = choix.reactifs.filter(Boolean);
  const restants = stock.reactifs.filter((o) => o.selectionnable && !pris.includes(o.cle)).length;
  const nbListes = Math.max(1, pris.length + (restants > 0 && pris.length > 0 ? 1 : 0));
  const majReactif = (i: number, cle: string) => {
    const r = [...pris];
    if (cle) r[i] = cle; else r.splice(i, 1);
    onChange({ ...choix, reactifs: r.filter(Boolean) });
  };
  const G: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px 16px' };
  return (
    <div style={G}>
      {Array.from({ length: nbListes }).map((_, i) => (
        <ChoixLotStock key={`r${i}`} id={`${prefixe}-r${i}`} label={i === 0 ? 'Réactif' : 'Autre réactif (facultatif)'} requis={i === 0}
          options={stock.reactifs} valeur={pris[i] || ''} exclure={pris.filter((_, j) => j !== i)}
          onChange={(v) => majReactif(i, v)} onDestocke={onDestocke} />
      ))}
      {stock.calibrants.length === 0 && (
        <ChoixLotStock id={`${prefixe}-c`} label="Calibrant" requis options={[]} valeur="" onChange={() => {}} onDestocke={onDestocke} />
      )}
      {stock.calibrants.map((g) => (
        <ChoixLotStock key={`c${g.niveau}`} id={`${prefixe}-c${g.niveau}`} label={g.niveau ? `Calibrant — niveau ${g.niveau}` : 'Calibrant'} requis
          options={g.options} valeur={choix.calibrants[g.niveau] || ''}
          onChange={(v) => onChange({ ...choix, calibrants: { ...choix.calibrants, [g.niveau]: v } })} onDestocke={onDestocke} />
      ))}
      <ChoixLotStock id={`${prefixe}-q`} label={controleRequis ? 'Contrôle qualité utilisé' : 'Contrôle qualité utilisé (facultatif)'} requis={controleRequis}
        options={stock.controles} valeur={choix.controle} onChange={(v) => onChange({ ...choix, controle: v })} onDestocke={onDestocke} />
    </div>
  );
}

/** Bandeau d'un appareil sans stock actif (Q8) : la saisie libre reste, et se dit. */
export function BandeauHorsStock({ equipement }: { equipement: string }) {
  return (
    <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: 12.5, color: 'var(--text-body)' }}>
      <strong style={{ color: 'var(--text-strong)' }}>Hors stock — saisie libre.</strong>{' '}
      Les réactifs et calibrants de {equipement} ne sont pas encore dans le stock de Nafis : lots et
      péremptions se saisissent à la main, et la calibration est marquée « saisie libre ».
    </div>
  );
}

/** Le stock de l'appareil en cours de lecture, ou injoignable : on le DIT,
 *  plutôt que d'afficher des champs que le moteur refuserait peut-être. */
export function EtatStock({ etat, onReessayer }: { etat: 'vide' | 'charge' | 'pret' | 'echec'; onReessayer: () => void }) {
  if (etat === 'charge') {
    return <div aria-busy="true" style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du stock de l’appareil…</div>;
  }
  if (etat === 'echec') {
    return (
      <div role="alert" style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5, color: 'var(--red-600)' }}>
        Le stock de l’appareil n’a pas pu être lu : réactif, calibrant et contrôle ne peuvent pas être choisis.
        <Bouton taille="sm" onClick={onReessayer}>Réessayer</Bouton>
      </div>
    );
  }
  return null;
}
